import React, { useMemo } from "react";
import {
  Edit,
  Trash2,
  Copy,
  Eye,
  Plus,
  Search,
  Funnel,
  Loader2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const parseDateTime = (date, time) => {
  if (!date) return null;
  const isoString = `${date}${time ? `T${time}` : "T00:00"}`;
  const parsed = new Date(isoString);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const deriveStatus = (event) => {
  const scheduledAt = parseDateTime(event.date, event.time);
  const now = new Date();

  if (!scheduledAt) {
    return "Scheduled";
  }

  if (scheduledAt.getTime() < now.getTime()) {
    return "Expired";
  }

  return event.needVolunteers ? "Active" : "Scheduled";
};

const statusClasses = (status) => {
  switch ((status || "").toLowerCase()) {
    case "draft":
      return "bg-gray-500 text-white";
    case "active":
      return "bg-[#00AC0B] text-white";
    case "scheduled":
      return "bg-[#FFC400] text-[#121717]";
    case "expired":
      return "bg-[#DD0000] text-white";
    default:
      return "bg-sky-100 text-sky-700 ring-1 ring-sky-200";
  }
};

const formatDate = (value) => {
  if (!value) return "—";

  const parsed = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(parsed?.getTime?.())) {
    return "—";
  }

  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export default function EventTable({
  data = [],
  onAction,
  isLoading = false,
  errorMessage,
}) {
  const navigate = useNavigate();
  const events = useMemo(
    () =>
      data.map((event) => ({
        ...event,
        status: event.status ?? deriveStatus(event),
        datePostedDisplay: formatDate(event.datePosted ?? event.createdAt),
        eventDateDisplay: formatDate(
          event.date ?? event.eventDate ?? event.expiryDate,
        ),
      })),
    [data],
  );
  const hasEvents = events.length > 0;

  const handleDetail = (event) => {
    navigate(`/dashboard/manage-event-detail/${event.id}`, {
      state: { event },
    });
  };

  const handleEdit = (event) => {
    if (typeof onAction === "function") {
      onAction("edit", event);
    }
    navigate(`/dashboard/manage-events/${event.id}/edit`, {
      state: { event },
    });
  };

  const handleDuplicate = (event) => {
    if (typeof onAction === "function") {
      onAction("duplicate", event);
    }
  };

  const handleDelete = (event) => {
    if (typeof onAction === "function") {
      onAction("delete", event);
    }
  };

  return (
    <div className="w-[95%] mx-auto my-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-popinsBold text-2xl">Manage Events</h1>
          <p className="font-popinsRegular text-textColor mt-1 text-sm">
            View, edit, or remove public announcements.
          </p>
        </div>
        <Link to={"/dashboard/post-event"}>
          <button className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-base mt-5 rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] active:scale-95 active:shadow-inner focus:outline-none w-full flex items-center px-5 gap-x-2">
            <span className="bg-white p-1 rounded-md">
              <Plus size={14} className="text-themeColor" />
            </span>
            <span>Post Event</span>
            <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
          </button>
        </Link>
      </div>
      <div className="flex gap-x-5 mt-6">
        <Search size={25} color="#121717" />
        <Funnel size={25} color="#121717" />
      </div>
      <div className="overflow-x-auto mt-5 rounded-xl">
        <table className="hidden md:table w-full text-sm border border-border rounded-xl">
          <thead className="bg-white/50 sticky top-0 border-b border-border !font-popinsRegular">
            <tr>
              <th className="text-left w-[35%] py-3 px-4">Title</th>
              <th className="w-[15%] py-3 text-center">Status</th>
              <th className="text-center w-[15%] py-3">Date posted</th>
              <th className="text-center w-[15%] py-3">Event date</th>
              <th className="text-center w-[20%] py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center">
                  <div className="flex justify-center items-center gap-2 text-black/50">
                    <Loader2 className="animate-spin" size={20} />
                    <span className="text-sm"></span>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && errorMessage && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-sm text-red-500"
                >
                  {errorMessage}
                </td>
              </tr>
            )}
            {!isLoading && !errorMessage && !hasEvents && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-sm text-black/50"
                >
                  No events posted yet. Use the button above to create your
                  first event.
                </td>
              </tr>
            )}
            {!isLoading &&
              !errorMessage &&
              hasEvents &&
              events.map((row) => (
                <tr
                  key={row.id}
                  className="bg-white/60 font-popinsMedium border-b border-border hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    <p>{row.title}</p>
                    <p className="text-xs text-black/50 mt-1">
                      {row.location} • {row.typeName}
                    </p>
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={`flex justify-center items-center gap-2 px-2 py-2 rounded-lg text-sm font-popinsMedium ${statusClasses(
                        row.status,
                      )}`}
                    >
                      <span
                        className="w-2 h-2 rounded-full bg-white/70"
                        aria-hidden
                      ></span>
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 text-center text-black/50">
                    {row.datePostedDisplay}
                  </td>
                  <td className="py-3 text-center text-black/50">
                    {row.eventDateDisplay}
                  </td>
                  <td className="px-4 py-3 align-middle text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => handleDetail(row)}
                        title="View"
                        className="p-2 rounded-md cursor-pointer text-themeColor hover:bg-themeColor/10 transition-all"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleDuplicate(row)}
                        title="Duplicate"
                        className="p-2 rounded-md text-[#D5B500] hover:bg-[#D5B500]/10 transition-all"
                      >
                        <Copy size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(row)}
                        title="Edit"
                        className="p-2 rounded-md text-blue-600 hover:bg-blue-50 transition-all"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        title="Delete"
                        className="p-2 rounded-md hover:bg-red-50 text-red-600 transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="md:hidden space-y-3">
          {isLoading && (
            <div className="bg-white/80 p-4 rounded-xl border border-border">
              <div className="flex justify-center items-center gap-2 text-black/50">
                <Loader2 className="animate-spin" size={20} />
                <span className="text-sm"></span>
              </div>
            </div>
          )}
          {!isLoading && errorMessage && (
            <div className="bg-white/80 p-4 rounded-xl border border-border text-center text-sm text-red-500">
              {errorMessage}
            </div>
          )}
          {!isLoading && !errorMessage && !hasEvents && (
            <div className="bg-white/80 p-4 rounded-xl border border-border text-center text-sm text-black/50">
              No events posted yet. Tap the button above to create one.
            </div>
          )}
          {!isLoading &&
            !errorMessage &&
            hasEvents &&
            events.map((row) => (
              <div
                key={row.id}
                className="bg-white/80 p-4 rounded-xl shadow-sm border border-border hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={() => handleDetail(row)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-popinsMedium text-sm">{row.title}</p>
                    <p className="text-xs text-black/50 mt-1">
                      {row.location} • {row.typeName}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-popinsMedium ${statusClasses(
                          row.status,
                        )}`}
                      >
                        {row.status}
                      </span>
                      <div className="text-sm text-black/50 font-popinsMedium">
                        Posted: {row.datePostedDisplay}
                      </div>
                      <div className="text-sm text-black/50 font-popinsMedium">
                        Event: {row.eventDateDisplay}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDetail(row);
                        }}
                        title="View"
                        className="p-2 rounded-md text-themeColor hover:bg-themeColor/10 transition-all"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDuplicate(row);
                        }}
                        title="Duplicate"
                        className="p-2 rounded-md text-[#D5B500] hover:bg-[#D5B500]/10 transition-all"
                      >
                        <Copy size={18} />
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEdit(row);
                        }}
                        title="Edit"
                        className="p-2 rounded-md text-blue-600 hover:bg-blue-50 transition-all"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDelete(row);
                        }}
                        title="Delete"
                        className="p-2 rounded-md hover:bg-red-50 text-red-600 transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
